import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  InputAdornment,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { useDebouncedValue } from "@src/appV2/lib/utils/useDebouncedValue";
import { Text } from "@src/appV2/redesign/components/Text";
import { berlinTheme } from "@src/appV2/redesign/theme/theme";
import { type Agent, useSearchAgents } from "@src/appV2/User/api/useSearchAgents";
import { type Facility, useSearchFacilities } from "@src/appV2/User/api/useSearchFacilities";
import {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { OmnisearchResultsList } from "./OmnisearchResultsList";

const SEARCH_LIMIT = 5;

interface OmnisearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectAgent: (agent: Agent) => void;
  onSelectFacility: (facility: Facility) => void;
}

export type SearchResultItem =
  | { type: "agent"; data: Agent }
  | { type: "facility"; data: Facility };

function getShortcutHint(): string {
  const isMac = navigator.platform.toUpperCase().includes("MAC");

  return isMac ? "⌘K" : "Ctrl+K";
}

export function OmnisearchDialog(props: OmnisearchDialogProps) {
  const { open, onClose, onSelectAgent, onSelectFacility } = props;
  const [searchInput, setSearchInput] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const [debouncedSearchInput] = useDebouncedValue(searchInput, 200);

  const { data: agentsData, isLoading: isLoadingAgents } = useSearchAgents({
    searchInput: debouncedSearchInput,
    limit: SEARCH_LIMIT,
  });

  const { data: facilitiesData, isLoading: isLoadingFacilities } = useSearchFacilities({
    searchInput: debouncedSearchInput,
    limit: SEARCH_LIMIT,
  });

  const { agents, facilities, allResults } = useMemo(() => {
    const agentsList = agentsData?.pages.flatMap((page) => page.list) ?? [];
    const facilitiesList = facilitiesData?.pages.flatMap((page) => page.list) ?? [];

    const results: SearchResultItem[] = [
      ...agentsList.map((agent): SearchResultItem => ({ type: "agent", data: agent })),
      ...facilitiesList.map((facility): SearchResultItem => ({ type: "facility", data: facility })),
    ];

    return { agents: agentsList, facilities: facilitiesList, allResults: results };
  }, [agentsData, facilitiesData]);

  const isLoading = isLoadingAgents || isLoadingFacilities;
  const hasResults = agents.length > 0 || facilities.length > 0;
  const hasSearchInput = debouncedSearchInput.length > 0;

  const handleSelect = useCallback(
    (item: SearchResultItem) => {
      if (item.type === "agent") {
        onSelectAgent(item.data);
      } else {
        onSelectFacility(item.data);
      }

      onClose();
      setSearchInput("");
      setSelectedIndex(0);
    },
    [onSelectAgent, onSelectFacility, onClose]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedIndex((previous) => Math.min(previous + 1, allResults.length - 1));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((previous) => Math.max(previous - 1, 0));
      } else if (event.key === "Enter" && allResults.length > 0) {
        event.preventDefault();
        const selectedItem = allResults[selectedIndex];
        if (selectedItem) {
          handleSelect(selectedItem);
        }
      }
    },
    [allResults, selectedIndex, handleSelect]
  );

  // Reset selected index when search results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [debouncedSearchInput]);

  const handleClose = useCallback(() => {
    onClose();
    setSearchInput("");
    setSelectedIndex(0);
  }, [onClose]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  return (
    <ThemeProvider theme={berlinTheme}>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        PaperProps={{
          sx: {
            position: "fixed",
            top: "15%",
            margin: "0",
            borderRadius: 2,
            maxHeight: "70vh",
          },
        }}
        onClose={handleClose}
      >
        <DialogContent sx={{ p: "8px" }}>
          <TextField
            // While autofocus is generally discouraged, it is necessary here to ensure the input is focused when the dialog opens.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            fullWidth
            inputRef={inputRef}
            placeholder="Search professionals, workplaces..."
            value={searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Text
                    sx={{
                      color: "text.secondary",
                      backgroundColor: "action.hover",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontFamily: "monospace",
                    }}
                    variant="caption"
                  >
                    {getShortcutHint()}
                  </Text>
                </InputAdornment>
              ),
              sx: {
                py: 1.5,
                px: 2,
                "& fieldset": { border: "none" },
              },
            }}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />

          {hasSearchInput && (
            <Box sx={{ borderTop: 1, borderColor: "divider" }}>
              {isLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!isLoading && !hasResults && (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <Text color="text.secondary">
                    No results found for &quot;{debouncedSearchInput}&quot;
                  </Text>
                </Box>
              )}

              {!isLoading && hasResults && (
                <OmnisearchResultsList
                  agents={agents}
                  facilities={facilities}
                  selectedIndex={selectedIndex}
                  onSelect={handleSelect}
                />
              )}
            </Box>
          )}

          {!hasSearchInput && (
            <Box sx={{ py: 3, px: 2, borderTop: 1, borderColor: "divider" }}>
              <Text color="text.secondary" sx={{ textAlign: "center" }} variant="body2">
                Start typing to search for professionals or workplaces
              </Text>
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2 }}>
                <Text color="text.secondary" variant="caption">
                  ↑↓ Navigate
                </Text>
                <Text color="text.secondary" variant="caption">
                  ↵ Select
                </Text>
                <Text color="text.secondary" variant="caption">
                  Esc Close
                </Text>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}
